/**
 * The smaller settings pages: preferences (theme), notifications (this device's push, the kinds of thing that notify
 * me) and the placeholders that give the structure a home until the features ship (integrations, security, data,
 * stickers & GIFs — the stickers feature mounts its own settings in that slot).
 */
import { Notify, type DndViewModel, type NotificationsViewModel } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import type { ReactNode } from "react";
import { useSyncExternalStore } from "react";
import { Button, Icon, Stack, Text, Toggle } from "../../primitives/index.ts";
import { usePreferences, type ThemePreference } from "../../context/AppContext.tsx";
import type { NotificationSoundControl } from "../../context/SettingsContext.tsx";
import { useT } from "../../i18n/I18nProvider.tsx";
import { DndSettings } from "./DndSettings.tsx";
import { FieldLabel, FilterPill, SettingsSection } from "./SettingsKit.tsx";

const THEMES: ThemePreference[] = ["system", "light", "dark"];

/**
 * Usage-analytics consent, where the host offers analytics (web: GA4 with consent mode). `byDefault`: on by the
 * regional default, not by a choice (core logic/analyticsConsent) — the switch says "On (you can turn this off)".
 */
export type AnalyticsPreference = { granted: boolean; byDefault?: boolean; onChange: (granted: boolean) => void };

export function PreferencesSettings({ analytics }: { analytics?: AnalyticsPreference } = {}) {
  const t = useT();
  const prefs = usePreferences();
  const label = (th: ThemePreference) => (th === "system" ? t("profile.theme.system") : th === "light" ? t("profile.theme.light") : t("profile.theme.dark"));
  return (
    <Stack gap="6" testID="preferences-settings">
      <SettingsSection title={t("settings.preferences.appearance")} testID="preferences-appearance">
        <Stack gap="1.5">
          <FieldLabel label={t("profile.theme")} />
          <Stack direction="horizontal" gap="2" wrap>
            {THEMES.map((th) => (
              <FilterPill key={th} label={label(th)} selected={prefs.theme === th} onPress={() => prefs.setTheme(th)} testID={`preferences-theme-${th}`} />
            ))}
          </Stack>
        </Stack>
      </SettingsSection>
      {analytics === undefined ? null : (
        <SettingsSection title={t("settings.preferences.privacy")} testID="preferences-privacy">
          <Stack gap="1">
            <Toggle label={t("profile.analytics")} value={analytics.granted} onChange={analytics.onChange} testID="preferences-analytics" />
            {analytics.byDefault === true && analytics.granted ? (
              <Text variant="footnote" color="text.secondary" testID="preferences-analytics-default">
                {t("settings.preferences.analytics.onByDefault")}
              </Text>
            ) : null}
            <Text variant="footnote" color="text.tertiary">
              {t("settings.preferences.analytics.body")}
            </Text>
          </Stack>
        </SettingsSection>
      )}
    </Stack>
  );
}

const noop = () => () => {};
const none = () => null;

/** `kinds`: what notifies me, and New email: the sections about what reaches me, between this device and its sound. */
export function NotificationsSettings({ notifications, sound, dnd, kinds }: { notifications?: NotificationsViewModel; sound?: NotificationSoundControl; dnd?: DndViewModel; kinds?: ReactNode }) {
  const t = useT();
  const state = useSyncExternalStore(notifications?.subscribe ?? noop, notifications?.getSnapshot ?? none, notifications?.getSnapshot ?? none);
  const status = state?.status ?? "unsupported";
  return (
    <Stack gap="6" testID="notifications-settings">
      {dnd === undefined ? null : <DndSettings vm={dnd} />}
      <SettingsSection title={t("settings.notifications.device")} testID="notifications-device">
        {notifications === undefined || state === null || status === "unsupported" || status === "unconfigured" ? (
          <Text color="text.secondary">{t("settings.notifications.unsupported")}</Text>
        ) : status === "blocked" ? (
          <Stack direction="horizontal" align="center" gap="2">
            <Icon name="bellOff" size="icon.md" color="status.warning" />
            <Text color="text.secondary">{t("profile.notifications.blocked")}</Text>
          </Stack>
        ) : status === "on" ? (
          <Stack direction="horizontal" align="center" gap="2">
            <Icon name="bell" size="icon.md" color="status.success" />
            <Stack grow>
              <Text>{t("profile.notifications.on")}</Text>
            </Stack>
            <Button label={t("settings.notifications.disable")} variant="secondary" size="sm" onPress={() => void notifications.disable()} testID="notifications-disable" />
          </Stack>
        ) : (
          <Stack direction="horizontal" align="center" gap="2">
            <Icon name="bellOff" size="icon.md" color="text.secondary" />
            <Stack grow>
              <Text>{state.failed ? t("profile.notifications.retry") : t("profile.notifications.enable")}</Text>
            </Stack>
            <Button label={t("profile.notifications.enable")} variant="primary" size="sm" loading={state.busy} onPress={() => void notifications.enable()} testID="notifications-enable" />
          </Stack>
        )}
      </SettingsSection>
      {kinds}
      {sound === undefined ? null : <NotificationSoundSection sound={sound} />}
    </Stack>
  );
}

const SOUND_LABEL = {
  knock: "settings.notifications.sound.knock",
  pop: "settings.notifications.sound.pop",
  chime: "settings.notifications.sound.chime",
  ding: "settings.notifications.sound.ding",
  pluck: "settings.notifications.sound.pluck",
} as const satisfies Record<Notify.NotificationSound, string>;

/** "Notification sound": on/off, the sound (picking one plays it), and Play to hear the current pick again. */
function NotificationSoundSection({ sound }: { sound: NotificationSoundControl }) {
  const t = useT();
  const setting = useSyncExternalStore(sound.subscribe, sound.getSnapshot, sound.getSnapshot);
  return (
    <SettingsSection title={t("settings.notifications.sound")} testID="notifications-sound">
      <Stack gap="1">
        <Toggle label={t("settings.notifications.sound.toggle")} value={setting.on} onChange={(on) => sound.set({ ...setting, on })} testID="notifications-sound-toggle" />
        <Text variant="footnote" color="text.tertiary">
          {t("settings.notifications.sound.body")}
        </Text>
      </Stack>
      {setting.on ? (
        <Stack direction="horizontal" align="center" gap="2" wrap>
          {Notify.NOTIFICATION_SOUNDS.map((s) => (
            <FilterPill
              key={s}
              label={t(SOUND_LABEL[s])}
              selected={setting.sound === s}
              onPress={() => {
                sound.set({ ...setting, sound: s });
                sound.preview(s);
              }}
              testID={`notifications-sound-${s}`}
            />
          ))}
          <Button label={t("settings.notifications.sound.preview")} icon="play" variant="secondary" size="sm" onPress={() => sound.preview(setting.sound)} testID="notifications-sound-preview" />
        </Stack>
      ) : null}
    </SettingsSection>
  );
}

/** A section whose feature isn't here yet: icon, what it will hold, "Coming soon" — or a feature's own panel. */
export function PlaceholderSettings({ icon, title, body, children, testID }: { icon: IconName; title: string; body: string; children?: ReactNode; testID: string }) {
  const t = useT();
  return (
    <Stack gap="4" testID={testID}>
      <Stack direction="horizontal" align="center" gap="3" padding="4" radius="lg" border="border.subtle" background="bg.surface">
        <Stack padding="3" radius="lg" background="accent.subtle">
          <Icon name={icon} size="icon.lg" color="accent.text" />
        </Stack>
        <Stack grow gap="0.5">
          <Text variant="headline" weight="semibold">
            {title}
          </Text>
          <Text variant="footnote" color="text.secondary">
            {body}
          </Text>
        </Stack>
        {children === undefined ? (
          <Stack paddingX="2" paddingY="0.5" radius="full" background="chip.bg">
            <Text variant="caption" weight="medium" color="chip.fg">
              {t("settings.comingSoonBadge")}
            </Text>
          </Stack>
        ) : null}
      </Stack>
      {children}
    </Stack>
  );
}
